import { Capacitor } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Configuration options for downloading a file.
 */
export interface FileDownloadProps {
  /**
   * The source URL of the file to download.
   */
  url: URL | string;
  /**
   * The desired filename for the saved file (e.g., "report.pdf"). This name
   * will be used in the download prompt or share sheet.
   */
  filename: string;
}

/**
 * Initiates a file download in a browser environment by creating a temporary,
 * invisible anchor link.
 *
 * It fetches the resource as a `Blob`, creates an object URL, and
 * programmatically clicks the link to trigger the browser's download behavior.
 * This method is preferred over `window.open` because it allows forcing a
 * specific `filename` via the HTML5 download attribute.
 */
const downloadFileWeb = async ({ url, filename }: FileDownloadProps) => {
  // Fetch the file
  const response = await fetch(url);
  const blob = await response.blob();

  // Create a temporary URL for the blob
  const objectUrl = URL.createObjectURL(blob);

  // Create an invisible link element
  const link = document.createElement('a');
  link.href = objectUrl;

  // Set a custom filename
  if (filename) {
    link.download = filename;
  }

  // Append an invisible link to body, click it, and cleanup
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke the object URL to free memory
  URL.revokeObjectURL(objectUrl);
};

/**
 * Handles the download process on native mobile platforms (iOS/Android).
 *
 * Since direct downloads behave differently on mobile, this function:
 * 1. Resolves a path in the app's temporary cache directory.
 * 2. Downloads the file to that location using `FileTransfer`.
 * 3. Opens the native Share sheet, allowing the user to save to the filesystem
 *    or open in another app.
 */
const downloadFileNative = async ({ url, filename }: FileDownloadProps) => {
  // Get the full file path using Filesystem
  const { uri } = await Filesystem.getUri({
    directory: Directory.Cache,
    path: filename,
  });

  // Download the file to the specified path
  await FileTransfer.downloadFile({
    url: url.toString(),
    path: uri,
  });

  // Share the file, which opens the native share dialog
  await Share.share({ url: uri });

  // Return the file path
  return uri;
};

/**
 * The main entry point for downloading files across Web and Native platforms.
 *
 * - **Native (iOS/Android):** Downloads to cache and opens the Share Sheet.
 * - **Web:** Triggers a browser download with a forced filename.
 * - **Fallback:** Opens the URL in a new tab if the specific method fails.
 */
export const downloadFile = async (props: FileDownloadProps) => {
  try {
    if (Capacitor.isNativePlatform()) {
      return downloadFileNative(props);
    }
    return downloadFileWeb(props);
  } catch (error) {
    console.error('Failed to download file', error);
    // Fallback to opening the url in new tab
    window.open(props.url, '_blank');
  }
};
